import type { BinaryOp, Expr, PathSegment } from "./ast.js";

export class ExpressionError extends Error {
  constructor(
    message: string,
    public readonly position: number,
    public readonly source: string
  ) {
    super(message);
    this.name = "ExpressionError";
  }
}

type Token =
  | { type: "num"; value: number; pos: number }
  | { type: "str"; value: string; pos: number }
  | { type: "ident"; value: string; pos: number }
  | { type: "op"; value: string; pos: number }
  | { type: "eof"; pos: number };

const MAX_LENGTH = 2000;
const MAX_DEPTH = 32;
const KEYWORDS = new Set(["and", "or", "not", "in", "true", "false", "null"]);
const MULTI_OPS = ["==", "!=", "<=", ">="];
const SINGLE_OPS = new Set("+-*/%<>()[]|:?,.".split(""));

export function tokenize(src: string): Token[] {
  if (src.length > MAX_LENGTH) throw new ExpressionError(`Expression is too long (max ${MAX_LENGTH} characters)`, 0, src);
  const tokens: Token[] = [];
  let i = 0;
  while (i < src.length) {
    const ch = src[i]!;
    if (/\s/.test(ch)) {
      i++;
      continue;
    }
    const pos = i;
    if (/[0-9]/.test(ch) || (ch === "." && /[0-9]/.test(src[i + 1] ?? ""))) {
      let j = i;
      while (j < src.length && /[0-9_]/.test(src[j]!)) j++;
      if (src[j] === "." && /[0-9]/.test(src[j + 1] ?? "")) {
        j++;
        while (j < src.length && /[0-9]/.test(src[j]!)) j++;
      }
      if ((src[j] === "e" || src[j] === "E") && /[0-9+-]/.test(src[j + 1] ?? "")) {
        j += 2;
        while (j < src.length && /[0-9]/.test(src[j]!)) j++;
      }
      tokens.push({ type: "num", value: Number(src.slice(i, j).replace(/_/g, "")), pos });
      i = j;
      continue;
    }
    if (ch === '"' || ch === "'") {
      let j = i + 1;
      let out = "";
      while (j < src.length && src[j] !== ch) {
        if (src[j] === "\\" && j + 1 < src.length) {
          const n = src[j + 1]!;
          out += n === "n" ? "\n" : n === "t" ? "\t" : n;
          j += 2;
        } else {
          out += src[j];
          j++;
        }
      }
      if (j >= src.length) throw new ExpressionError("Unterminated string", pos, src);
      tokens.push({ type: "str", value: out, pos });
      i = j + 1;
      continue;
    }
    if (/[A-Za-z_$]/.test(ch)) {
      let j = i;
      while (j < src.length && /[A-Za-z0-9_$]/.test(src[j]!)) j++;
      tokens.push({ type: "ident", value: src.slice(i, j), pos });
      i = j;
      continue;
    }
    const two = src.slice(i, i + 2);
    if (MULTI_OPS.includes(two)) {
      tokens.push({ type: "op", value: two, pos });
      i += 2;
      continue;
    }
    if (ch === "=" && src[i + 1] !== "=") {
      // Be forgiving: a single "=" reads as equality.
      tokens.push({ type: "op", value: "==", pos });
      i++;
      continue;
    }
    if (ch === "&" && src[i + 1] === "&") {
      tokens.push({ type: "ident", value: "and", pos });
      i += 2;
      continue;
    }
    if (ch === "|" && src[i + 1] === "|") {
      tokens.push({ type: "ident", value: "or", pos });
      i += 2;
      continue;
    }
    if (ch === "!" ) {
      tokens.push({ type: "ident", value: "not", pos });
      i++;
      continue;
    }
    if (SINGLE_OPS.has(ch)) {
      tokens.push({ type: "op", value: ch, pos });
      i++;
      continue;
    }
    throw new ExpressionError(`Unexpected character "${ch}"`, pos, src);
  }
  tokens.push({ type: "eof", pos: src.length });
  return tokens;
}

/**
 * Pratt parser. Precedence (low → high):
 *   pipe  |
 *   conditional ?:
 *   or
 *   and
 *   not (prefix)
 *   == != in
 *   < <= > >=
 *   + -
 *   * / %
 *   unary -
 *   postfix . [ (
 */
class Parser {
  private i = 0;
  private depth = 0;
  constructor(
    private readonly tokens: Token[],
    private readonly src: string
  ) {}

  parse(): Expr {
    const e = this.parsePipe();
    if (this.peek().type !== "eof") this.fail(`Unexpected "${this.describe(this.peek())}"`);
    return e;
  }

  private peek(): Token {
    return this.tokens[this.i]!;
  }
  private next(): Token {
    return this.tokens[this.i++]!;
  }
  private isOp(v: string): boolean {
    const t = this.peek();
    return t.type === "op" && t.value === v;
  }
  private isKw(v: string): boolean {
    const t = this.peek();
    return t.type === "ident" && t.value === v;
  }
  private expectOp(v: string) {
    if (!this.isOp(v)) this.fail(`Expected "${v}" but found "${this.describe(this.peek())}"`);
    this.next();
  }
  private describe(t: Token): string {
    return t.type === "eof" ? "end of expression" : t.type === "str" ? `"${t.value}"` : String(t.value);
  }
  private fail(msg: string): never {
    throw new ExpressionError(msg, this.peek().pos, this.src);
  }
  private enter() {
    if (++this.depth > MAX_DEPTH) this.fail("Expression is nested too deeply");
  }
  private leave() {
    this.depth--;
  }

  private parsePipe(): Expr {
    let e = this.parseConditional();
    while (this.isOp("|")) {
      this.next();
      const nameTok = this.next();
      if (nameTok.type !== "ident") this.fail("Expected a formatter name after |");
      const args: Expr[] = [];
      while (this.isOp(":")) {
        this.next();
        args.push(this.parseConditional());
      }
      e = { kind: "pipe", input: e, name: nameTok.value, args };
    }
    return e;
  }

  private parseConditional(): Expr {
    const test = this.parseOr();
    if (this.isOp("?")) {
      this.enter();
      this.next();
      const then = this.parseConditional();
      this.expectOp(":");
      const els = this.parseConditional();
      this.leave();
      return { kind: "conditional", test, then, else: els };
    }
    return test;
  }

  private parseOr(): Expr {
    let left = this.parseAnd();
    while (this.isKw("or")) {
      this.next();
      left = { kind: "binary", op: "or", left, right: this.parseAnd() };
    }
    return left;
  }

  private parseAnd(): Expr {
    let left = this.parseNot();
    while (this.isKw("and")) {
      this.next();
      left = { kind: "binary", op: "and", left, right: this.parseNot() };
    }
    return left;
  }

  private parseNot(): Expr {
    if (this.isKw("not")) {
      this.enter();
      this.next();
      const arg = this.parseNot();
      this.leave();
      return { kind: "unary", op: "not", arg };
    }
    return this.parseEquality();
  }

  private parseEquality(): Expr {
    let left = this.parseComparison();
    for (;;) {
      if (this.isOp("==") || this.isOp("!=")) {
        const op = this.next() as { value: BinaryOp };
        left = { kind: "binary", op: op.value, left, right: this.parseComparison() };
      } else if (this.isKw("in")) {
        this.next();
        left = { kind: "binary", op: "in", left, right: this.parseComparison() };
      } else return left;
    }
  }

  private parseComparison(): Expr {
    let left = this.parseAdditive();
    while (this.isOp("<") || this.isOp("<=") || this.isOp(">") || this.isOp(">=")) {
      const op = this.next() as { value: BinaryOp };
      left = { kind: "binary", op: op.value, left, right: this.parseAdditive() };
    }
    return left;
  }

  private parseAdditive(): Expr {
    let left = this.parseMultiplicative();
    while (this.isOp("+") || this.isOp("-")) {
      const op = this.next() as { value: BinaryOp };
      left = { kind: "binary", op: op.value, left, right: this.parseMultiplicative() };
    }
    return left;
  }

  private parseMultiplicative(): Expr {
    let left = this.parseUnary();
    while (this.isOp("*") || this.isOp("/") || this.isOp("%")) {
      const op = this.next() as { value: BinaryOp };
      left = { kind: "binary", op: op.value, left, right: this.parseUnary() };
    }
    return left;
  }

  private parseUnary(): Expr {
    if (this.isOp("-")) {
      this.enter();
      this.next();
      const arg = this.parseUnary();
      this.leave();
      if (arg.kind === "literal" && typeof arg.value === "number") return { kind: "literal", value: -arg.value };
      return { kind: "unary", op: "-", arg };
    }
    return this.parsePostfix();
  }

  private parsePostfix(): Expr {
    let e = this.parsePrimary();
    for (;;) {
      if (this.isOp(".")) {
        this.next();
        const t = this.next();
        if (t.type !== "ident") this.fail("Expected a property name after .");
        e = appendSegment(e, { kind: "prop", name: t.value }, () => this.fail("Property access is only allowed on data paths"));
      } else if (this.isOp("[")) {
        this.next();
        const t = this.next();
        if (t.type !== "num" || !Number.isInteger(t.value) || t.value < 0) this.fail("Index must be a non-negative integer");
        this.expectOp("]");
        e = appendSegment(e, { kind: "index", index: t.value }, () => this.fail("Indexing is only allowed on data paths"));
      } else return e;
    }
  }

  private parsePrimary(): Expr {
    const t = this.next();
    switch (t.type) {
      case "num":
        return { kind: "literal", value: t.value };
      case "str":
        return { kind: "literal", value: t.value };
      case "ident": {
        if (t.value === "true") return { kind: "literal", value: true };
        if (t.value === "false") return { kind: "literal", value: false };
        if (t.value === "null") return { kind: "literal", value: null };
        if (KEYWORDS.has(t.value)) this.fail(`Unexpected keyword "${t.value}"`);
        if (this.isOp("(")) {
          this.enter();
          this.next();
          const args: Expr[] = [];
          if (!this.isOp(")")) {
            args.push(this.parsePipe());
            while (this.isOp(",")) {
              this.next();
              args.push(this.parsePipe());
            }
          }
          this.expectOp(")");
          this.leave();
          return { kind: "call", name: t.value, args };
        }
        return { kind: "path", segments: [{ kind: "prop", name: t.value }] };
      }
      case "op":
        if (t.value === "(") {
          this.enter();
          const e = this.parsePipe();
          this.expectOp(")");
          this.leave();
          return e;
        }
        if (t.value === "[") {
          this.enter();
          const items: Expr[] = [];
          if (!this.isOp("]")) {
            items.push(this.parsePipe());
            while (this.isOp(",")) {
              this.next();
              items.push(this.parsePipe());
            }
          }
          this.expectOp("]");
          this.leave();
          return { kind: "array", items };
        }
        this.fail(`Unexpected "${t.value}"`);
      // eslint-disable-next-line no-fallthrough
      case "eof":
        this.fail("Unexpected end of expression");
    }
  }
}

function appendSegment(e: Expr, seg: PathSegment, fail: () => never): Expr {
  if (e.kind !== "path") return fail();
  return { kind: "path", segments: [...e.segments, seg] };
}

const cache = new Map<string, Expr>();

/** Parses an expression. Results are cached; the AST must be treated as immutable. */
export function parseExpression(src: string): Expr {
  const key = src.trim();
  const hit = cache.get(key);
  if (hit) return hit;
  if (key.length === 0) throw new ExpressionError("Expression is empty", 0, src);
  const ast = new Parser(tokenize(key), key).parse();
  if (cache.size > 5000) cache.clear();
  cache.set(key, ast);
  return ast;
}

/** Returns the parse error for an expression, or null if it parses. */
export function checkExpressionSyntax(src: string): ExpressionError | null {
  try {
    parseExpression(src);
    return null;
  } catch (e) {
    return e instanceof ExpressionError ? e : new ExpressionError(String(e), 0, src);
  }
}
